/* import __COLOCATED_TEMPLATE__ from './test-settings-modal.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { PlaygroundSettings } from 'embercom/lib/fin-playground/settings';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import { tracked } from '@glimmer/tracking';
import type InboxSearchSuggestionsService from 'embercom/services/inbox-search-suggestions-service';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
// @ts-ignore
import PredicateGroup from 'predicates/models/predicate-group';
// @ts-ignore
import { copy } from 'ember-copy';
import { localCopy } from 'tracked-toolbox';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type IntlService from 'embercom/services/intl';
import {
  firstSegmentNameFromIds,
  shouldShowBrand,
  isBrandDisabled,
} from 'embercom/components/ai-agent/playground/settings/helpers';

interface TestSettingsModalArgs {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  playgroundSettings: PlaygroundSettings;
  withBrandDropdown?: boolean;
  withUserDropdown?: boolean;
  storedUser: UserSummary | null;
}

export default class TestSettingsModal extends Component<TestSettingsModalArgs> {
  @localCopy('args.storedUser') declare user: UserSummary | null;
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare store: Store;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare intl: IntlService;
  @tracked ruleset: $TSFixMe;
  @tracked inSearchMode = false;
  @tracked inAudienceSearchMode = false;
  @service declare appService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  constructor(owner: any, args: TestSettingsModalArgs) {
    super(owner, args);

    this.ruleset = this.setUpRuleset();
  }

  get displayUser() {
    return this.user;
  }

  get userTargetingEnabled() {
    return this.args.withUserDropdown;
  }

  get brandIsDisabled() {
    return isBrandDisabled(this.args.playgroundSettings);
  }

  get shouldShowBrandInModal() {
    return shouldShowBrand({
      withBrandDropdown: this.args.withBrandDropdown || false,
      usingAudienceTargeting: this.args.playgroundSettings.usingAudienceTargeting,
      isStandaloneApp: this.appService.app.isStandaloneApp,
    });
  }

  get shouldDisableSettingsReset() {
    return (
      !this.user &&
      !this.args.playgroundSettings.brandId &&
      !this.args.playgroundSettings.usingAudienceTargeting
    );
  }

  get canUseFinPlaygroundAudienceTargeting() {
    return this.appService.app.canUseFinPlaygroundAudienceTargeting;
  }

  get canUseFinPlaygroundTestAsSelector() {
    return this.appService.app.canUseFinPlaygroundTestAsSelector;
  }

  get selectedSegmentIds() {
    return this.args.playgroundSettings?.selectedContentSegmentIds || [];
  }

  get segments() {
    return this.aiContentSegmentsService.segments;
  }

  get firstSelectedSegmentName(): string | undefined {
    let firstSegmentName = firstSegmentNameFromIds(this.segments, this.selectedSegmentIds);
    if (this.selectedSegmentIds.length === 1) {
      return firstSegmentName;
    }

    return this.intl.t('ai-agent.playground.settings.audience-filter.additional-segments', {
      segmentName: firstSegmentName,
      count: this.selectedSegmentIds.length - 1,
    });
  }

  get selectedAudienceName() {
    return this.selectedSegmentIds?.length ? this.firstSelectedSegmentName : null;
  }

  @action
  save() {
    if (this.args.playgroundSettings.usingAudienceTargeting) {
      this.args.playgroundSettings.userId = null;
      this.args.playgroundSettings.brandId = null;
    } else {
      this.args.playgroundSettings.removeAllSegments();
    }

    this.args.onSave();

    this.inSearchMode = false;
    this.inAudienceSearchMode = false;
    this.onClose();

    let impersonatedUserTracking = this.args.playgroundSettings.userId
      ? 'real_user'
      : 'preview_user';

    this.intercomEventService.trackAnalyticsEvent({
      action: 'saved',
      object: 'playground_test_settings',
      place: 'playground_test_settings_modal',
      audience_targeting_mode: this.args.playgroundSettings.usingAudienceTargeting
        ? 'audience'
        : 'user',
      impersonation: this.args.playgroundSettings.usingAudienceTargeting
        ? null
        : impersonatedUserTracking,
      brand: this.args.playgroundSettings.brandId ? 'chose_brand' : 'default_brand',
    });
  }

  @action
  toggleSearchMode() {
    this.inSearchMode = !this.inSearchMode;

    if (this.inSearchMode) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'opened',
        object: 'impersonated_user_search',
        place: 'playground_test_settings_modal',
      });
    }
  }

  @action
  toggleAudienceSearchMode() {
    this.inAudienceSearchMode = !this.inAudienceSearchMode;

    if (this.inAudienceSearchMode) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'opened',
        object: 'impersonated_audience_search',
        place: 'playground_test_settings_modal',
      });
    }
  }

  @action
  onSelectAudienceMode() {
    // Clear user selection when switching to audience mode
    this.user = null;
    this.args.playgroundSettings.userId = null;

    // Reset brand selection when switching to audience mode
    this.args.playgroundSettings.brandId = null;

    // Reset user search filters when switching to audience mode
    this.resetUserSearchFilters();

    // Open audience search mode
    this.toggleAudienceSearchMode();
  }

  @action
  onSelectUserOrLead() {
    // Clear audiences when switching to user mode
    this.args.playgroundSettings.removeAllSegments();
    this.args.playgroundSettings.usingAudienceTargeting = false;

    this.toggleSearchMode();
  }

  @action
  onCancel() {
    this.args.playgroundSettings.rollbackChanges();
    this.onClose();
  }

  @action
  onClose() {
    // reset the user to the stored user
    // yes, even on save, as the user might cancel the save in the next modal
    // if the save is completed, the storedUser will reflect the newest value
    this.user = this.args.storedUser;

    this.inSearchMode = false;
    this.inAudienceSearchMode = false;
    this.args.onClose();
  }

  @action
  onSelectUser(user: UserSummary | null) {
    // Clear audiences when switching to user modes
    this.args.playgroundSettings.removeAllSegments();
    this.args.playgroundSettings.usingAudienceTargeting = false;

    if (user) {
      this.user = user;
      this.args.playgroundSettings.userId = user.id;
    } else {
      this.user = null;
      this.args.playgroundSettings.userId = null;
      this.args.playgroundSettings.brandId = null;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'user',
      place: 'playground_test_settings_modal',
    });
  }

  @action
  onSelectAudience(audience: { id: string }) {
    // Toggle the audience selection using the same pattern as AudienceDropdown
    this.args.playgroundSettings.addOrRemoveClickedSegment(audience.id);

    // Update the settings based on whether we have any selected segments
    if (this.args.playgroundSettings.selectedContentSegmentIds.length > 0) {
      this.args.playgroundSettings.usingAudienceTargeting = true;
      // Clear user selection when audience is selected
      this.user = null;
      this.args.playgroundSettings.userId = null;
    } else {
      // If no audiences are selected, revert to default
      this.args.playgroundSettings.usingAudienceTargeting = false;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'audience',
      place: 'playground_test_settings_modal',
    });
  }

  @action
  handleBrandSelection(brandId: string | null) {
    this.args.playgroundSettings.brandId = brandId;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'brand',
      place: 'playground_test_settings_modal',
    });
  }

  @action
  onUpdatePredicates(predicates: $TSFixMe) {
    this.ruleset.set('predicateGroup.predicates', copy(predicates));
    this.ruleset.fetchAudiencePreviewTask.perform();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'filters',
      place: 'playground_test_settings_modal',
    });
  }

  @action
  onUpdateRolePredicates(predicates: $TSFixMe) {
    this.ruleset.set('rolePredicateGroup.predicates', copy(predicates));
    this.ruleset.fetchAudiencePreviewTask.perform();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'role_filter',
      place: 'playground_test_settings_modal',
    });
  }

  setUpRuleset() {
    let newRuleset = this.store.createRecord('matching-system/ruleset');
    newRuleset.set('predicateGroup', this.store.createFragment('predicates/predicate-group'));
    newRuleset.set('clientPredicateGroup', this.store.createFragment('predicates/predicate-group'));
    // must properly define the default user roles to be all of them
    newRuleset.set(
      'rolePredicateGroup',
      this.store.createFragment('predicates/predicate-group', {
        predicates: PredicateGroup.convertRawPredicates(this.store, [
          {
            attribute: 'role',
            comparison: 'in',
            type: 'role',
            value: ['user_role', 'contact_role'],
          },
        ]),
      }),
    );
    newRuleset.set(
      'defaultPredicateGroup',
      this.store.createFragment('predicates/predicate-group'),
    );

    return newRuleset;
  }

  resetUserSearchFilters() {
    // Reset the ruleset to its default state (no custom filters)
    this.ruleset.set('predicateGroup', this.store.createFragment('predicates/predicate-group'));
    // Reset role predicates to default (all user roles)
    this.ruleset.set(
      'rolePredicateGroup',
      this.store.createFragment('predicates/predicate-group', {
        predicates: PredicateGroup.convertRawPredicates(this.store, [
          {
            attribute: 'role',
            comparison: 'in',
            type: 'role',
            value: ['user_role', 'contact_role'],
          },
        ]),
      }),
    );
  }

  @action
  resetToDefaultSettings() {
    this.onSelectUser(null);
    this.handleBrandSelection(null);
    this.args.playgroundSettings.usingAudienceTargeting = false;
    this.args.playgroundSettings.removeAllSegments();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'reset',
      object: 'playground_test_settings',
      place: 'playground_test_settings_modal',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::Modals::TestSettingsModal': typeof TestSettingsModal;
  }
}
